import type { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint } from "@sentry/react";

export type BeforeSend = (event: ErrorEvent, hint: EventHint) => PromiseLike<ErrorEvent | null> | ErrorEvent | null;

/**
 * Interface for errors that can be enriched with Sentry metadata.
 * Errors implementing this interface will have their tags, extra context,
 * and fingerprint automatically added to Sentry events via enrichedErrorBeforeSend.
 */
export interface EnrichedError {
  cause?: unknown;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  fingerprint?: string[];
}

export const composeBeforeSend = (...args: (BeforeSend | null | undefined)[]): BeforeSend => {
  const fns = args.filter((fn): fn is BeforeSend => typeof fn === "function");

  return async (event, hint) => {
    let result: ErrorEvent | null = event;
    for (const fn of fns) {
      result = await fn(result!, hint);
      if (!result) return null;
    }

    return result!;
  };
};

export type BeforeBreadcrumb = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => Breadcrumb | null;
export const composeBeforeBreadcrumb = (...args: (BeforeBreadcrumb | null | undefined)[]): BeforeBreadcrumb => {
  const fns = args.filter((fn): fn is BeforeBreadcrumb => typeof fn === "function");

  return (breadcrumb, hint) => {
    let result: Breadcrumb | null = breadcrumb;
    for (const fn of fns) {
      result = fn(result, hint);
      if (!result) return null;
    }

    return result;
  };
};

const hasDataNameValue = (attributes: NamedNodeMap, targetDataNameValue: string) => {
  for (const attribute in attributes) {
    if (attributes[attribute]?.name === "data-name" && targetDataNameValue === attributes[attribute].nodeValue) {
      return true;
    }
  }
  return false;
};

export const redactBreadcrumbs = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => {
  const elemAttributes = hint?.event?.srcElement?.attributes;

  if (hasDataNameValue(elemAttributes, "threadPreview")) {
    const redactedMessage = breadcrumb.message?.replace(/\[title="([^"]+)"\]/, '[title="<MESSAGE_REDACTED>"]');
    return {
      ...breadcrumb,
      message: redactedMessage,
    };
  }

  return breadcrumb;
};

const HEADERS_ALLOWLIST = [
  "x-dojo-request-id",
  "x-cache",
  "content-type",
  "content-length",
  "content-encoding",
  "via",
  "x-backend-group",
  "x-rate-limit",
  "x-response-time",
];

function getXhrHeaders(xhr: XMLHttpRequest, headers: string[]) {
  return headers.reduce(
    (acc, header) => ({ ...acc, [header]: xhr.getResponseHeader(header) }),
    {} as Record<string, string | null | undefined>,
  );
}

function getResponseHeaders(response: Response, headers: string[]) {
  return headers.reduce(
    (acc, header) => ({ ...acc, [header]: response.headers.get(header) }),
    {} as Record<string, string | null | undefined>,
  );
}

const currentLocationRoot = typeof window !== "undefined" ? window.location.origin : "https://api.classdojo.com";
const isSameOrigin = (url?: string) => {
  return url?.startsWith(currentLocationRoot) ?? false;
};

function handleXhrBreadcrumb(breadcrumb: Breadcrumb, xhr: XMLHttpRequest): Breadcrumb {
  return {
    ...breadcrumb,
    data: {
      ...breadcrumb.data,
      responseHeaders: {
        ...breadcrumb.data?.responseHeaders,
        ...getXhrHeaders(xhr, HEADERS_ALLOWLIST),
      },
    },
  };
}

function handleFetchBreadcrumb(breadcrumb: Breadcrumb, response: Response): Breadcrumb {
  return {
    ...breadcrumb,
    data: {
      ...breadcrumb.data,
      responseHeaders: {
        ...breadcrumb.data?.responseHeaders,
        ...getResponseHeaders(response, HEADERS_ALLOWLIST),
      },
    },
  };
}

export const addHeadersToBreadcrumbs = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null => {
  if (breadcrumb.category === "xhr") {
    const xhr = hint?.xhr;
    if (xhr && isSameOrigin(xhr.responseURL)) {
      return handleXhrBreadcrumb(breadcrumb, xhr);
    }
  }

  if (breadcrumb.category === "fetch") {
    const [url, _options] = (hint?.input as Parameters<typeof fetch>) || [];
    const response = hint?.response as Awaited<ReturnType<typeof fetch>>;

    if (!url || !isSameOrigin(url.toString()) || !response) return breadcrumb;

    return handleFetchBreadcrumb(breadcrumb, response);
  }

  return breadcrumb;
};

export const teamAttributionBeforeSend = (event: ErrorEvent, hint?: EventHint) => {
  const productArea = (hint?.originalException as Error & { productArea?: string })?.productArea;

  if (productArea) {
    event.tags = {
      productArea,
      ...event.tags,
    };
  }

  return event;
};

export const enrichedErrorBeforeSend = (event: ErrorEvent, hint?: EventHint) => {
  const exception = hint?.originalException;

  // Check if the exception has enrichment properties
  if (exception && typeof exception === "object") {
    const enrichedError = exception as EnrichedError;

    // Add tags from the error to the event
    if (enrichedError.tags) {
      event.tags = {
        ...event.tags,
        ...enrichedError.tags,
      };
    }

    // Add extra context from the error to the event
    if (enrichedError.extra) {
      event.extra = {
        ...event.extra,
        ...enrichedError.extra,
      };
    }

    // Add contexts from the error to the event
    if (enrichedError.contexts) {
      event.contexts = {
        ...event.contexts,
        ...enrichedError.contexts,
      };
    }

    // Set fingerprint from the error if provided
    if (enrichedError.fingerprint) {
      event.fingerprint = enrichedError.fingerprint;
    }
  }

  return event;
};

/** Sentry context key holding every resolved feature switch and its variant. */
export const FEATURE_SWITCHES_CONTEXT_KEY = "feature_switches";
/** Prefix for the per-switch tags, e.g. `fs_web_teach_asset_retry_service_worker`. */
export const FEATURE_SWITCH_TAG_PREFIX = "fs_";
/** Marks an event captured before any switch data existed, so a missing `fs_*` tag is
 * readable as "nothing to report yet" rather than "the reporting is broken". */
export const FEATURE_SWITCHES_STATUS_TAG = "feature_switches_status";

/**
 * Switches whose `off` variant is tagged too, making the control arm directly countable.
 *
 * Everything else is tagged only when it resolves to something other than `off`, because
 * tagging all 229 registry entries would put ~139 tags on every teach event. That filter
 * is fine for "which treatment was this user in", and wrong for "what did the control arm
 * do": with `off` untagged, an absent tag means *either* off *or* a session the switch
 * never applied to (logged out, ineligible audience, a release predating the switch).
 * Those populations have different error rates, so counting absence as the control arm
 * silently biases the comparison — measured at roughly 0.6 : 1 against the treatment arm
 * on error types a switch could not possibly affect, which is large enough to manufacture
 * a convincing effect out of nothing.
 *
 * Add a switch here while it is being measured; remove it when the readout is done. Each
 * entry costs one extra tag on every event from an app that resolves it.
 */
export const FULLY_TAGGED_FEATURE_SWITCHES: ReadonlySet<string> = new Set([
  "web_teach_asset_retry_service_worker",
  "web_home_asset_retry_service_worker",
]);

/** Returns the currently-resolved switches, or undefined before they have been fetched.
 * Supplied by `@web-monorepo/feature-switches`. */
export type FeatureSwitchesSource = () => Record<string, string> | undefined;

let featureSwitchesSource: FeatureSwitchesSource | undefined;

/**
 * Registers the read-only source that `featureSwitchBeforeSend` samples at send time.
 *
 * Injected rather than imported directly because `@web-monorepo/feature-switches` already
 * depends on this package (via `@web-monorepo/fetchers` too), so importing it here would
 * close a dependency cycle and pull react-query and the fetcher stack into telemetry's
 * boot graph. `@web-monorepo/feature-switches` calls this at module load with its own
 * `getResolvedFeatureSwitches`.
 */
export const setFeatureSwitchesSource = (source: FeatureSwitchesSource): void => {
  featureSwitchesSource = source;
};

/** Test-only: not re-exported from the package barrel. */
export const _testOnlyResetFeatureSwitchesSource = (): void => {
  featureSwitchesSource = undefined;
};

/**
 * Attaches feature-switch data to every outgoing event.
 *
 * This is the only mechanism that puts switch data on Sentry events. It runs at send time,
 * so the data does not depend on which components have rendered: previously the switches
 * were written to Sentry's shared scope as a side effect of a leaf component reading them,
 * which meant an error captured earlier in the page lifecycle (the dominant case being a
 * `moduleLoadError` from a stale chunk hash after a deploy) carried nothing. ~98% of the
 * events on one grouped issue were missing the tag for that reason. See WEBP-544.
 *
 * Switches resolving to "off" are tagged only when listed in
 * {@link FULLY_TAGGED_FEATURE_SWITCHES}; every other switch is tagged only when it resolves
 * to something else. The context carries the full set either way, so the untagged variants
 * are still readable on an individual event — just not aggregatable.
 */
export const featureSwitchBeforeSend = (event: ErrorEvent, _hint?: EventHint): ErrorEvent => {
  const switches = featureSwitchesSource?.();

  if (!switches) {
    event.tags = { [FEATURE_SWITCHES_STATUS_TAG]: "not_loaded", ...event.tags };
    event.contexts = {
      ...event.contexts,
      [FEATURE_SWITCHES_CONTEXT_KEY]: { [FEATURE_SWITCHES_STATUS_TAG]: "not_loaded" },
    };

    return event;
  }

  const switchTags: Record<string, string> = {};
  for (const [name, variant] of Object.entries(switches)) {
    if (variant === "off" && !FULLY_TAGGED_FEATURE_SWITCHES.has(name)) continue;
    switchTags[`${FEATURE_SWITCH_TAG_PREFIX}${name}`] = variant;
  }

  // Event-specific tags win: this step only fills in ambient state.
  event.tags = { ...switchTags, ...event.tags };
  event.contexts = { ...event.contexts, [FEATURE_SWITCHES_CONTEXT_KEY]: { ...switches } };

  return event;
};
